import { Button, Collapse, Descriptions, Drawer, Grid, message, Popconfirm, Space, Table, Tag } from "antd";
import styled from "styled-components";
import { colors } from "../../../utils/theme";
import { formatPrice, getPaymentStatus, getPaymentStatusColor } from "../../../functions/formatters";
import * as dayjs from "dayjs";
import { DATE_FORMAT_DD_MM_YYYY_HH_mm } from "../../../constants/DateConstant";
import { CURRENCY, DELIVERY_OPTIONS } from "../../../constants/DeliveryConstants";
import Text from "antd/lib/typography/Text";
import { Trans, useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { getProductCodeById } from "../../../functions/products";
import { useSelector } from "react-redux";
import { calculateTaxFromExVAT } from "../../../functions/orders";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  MailOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { resend_confirmation_email } from "../../../urls";
import { usePermissions } from "../../../data/hooks/usePermissions";
import { PERMISSIONS } from "../../../functions/permissions";
import { useFirestore } from "react-redux-firebase";
import { FIRESTORE_SHIPMENTS_TABLE } from "../../../constants/FirestoreConstant";
import { getOrderConfirmationStatus, ORDER_CONFIRMATION_STATUS } from "../../../functions/orderConfirmation";

const CONFIRMATION_TAGS = {
  [ORDER_CONFIRMATION_STATUS.SENT]: { color: "success", icon: <CheckCircleOutlined /> },
  [ORDER_CONFIRMATION_STATUS.PENDING]: { color: "processing", icon: <SyncOutlined spin /> },
  [ORDER_CONFIRMATION_STATUS.FAILED]: { color: "error", icon: <CloseCircleOutlined /> },
};

const OrderDrawer = ({ order, open, closeDrawer }) => {
  const { can } = usePermissions();
  const canManageOrders = can(PERMISSIONS.ORDERS_MANAGE);
  const { t } = useTranslation();
  const firestore = useFirestore();
  const products = useSelector((state) => state.firestoreReducer.ordered.products);
  // `order` is een kopie van het moment van openen. De live versie uit de store laat
  // de status van de orderbevestiging meteen bijwerken na "Bevestiging sturen".
  // Oudere orders vallen buiten de orderlijst in de store; dan blijft het de kopie.
  const liveOrder = useSelector((state) => state.firestoreReducer.data.orders?.[order?.id]);
  const [orderTax, setOrderTax] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [loadingShipments, setLoadingShipments] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  useEffect(() => {
    if (order) {
      setOrderTax(
        calculateTaxFromExVAT(
          order.products,
          order.delivery.cost,
          !!order.discount ? order.discount.percentage : null,
          !!order.discount ? order.discount.amount : null,
          !!order.discount ? order.discount.categories : null,
        ),
      );

      // Load shipments if shipmentIds exists
      if (order.shipmentIds && order.shipmentIds.length > 0) {
        loadShipments(order.shipmentIds);
      } else {
        setShipments([]);
      }
    }
  }, [order]);

  const loadShipments = async (shipmentIds) => {
    setLoadingShipments(true);
    try {
      const shipmentPromises = shipmentIds.map(async (shipmentId) => {
        const doc = await firestore.collection(FIRESTORE_SHIPMENTS_TABLE).doc(shipmentId).get();

        if (doc.exists) {
          return { id: doc.id, ...doc.data() };
        }
        return null;
      });

      const loadedShipments = await Promise.all(shipmentPromises);
      setShipments(loadedShipments.filter((s) => s !== null));
    } catch (error) {
      console.error("Error loading shipments:", error);
      setShipments([]);
    } finally {
      setLoadingShipments(false);
    }
  };

  const resendConfirmationMail = (order) => {
    axios
      .post(resend_confirmation_email, { order: order, customerEmail: order.customer.email })
      .then(() => messageApi.success(t("notifications.resend_confirmation_mail_success")))
      .catch(() => messageApi.error(t("notifications.resend_confirmation_mail_failure")));
  };

  const getEmailStatusColor = (status) => {
    switch (status) {
      case "sent":
        return "success";
      case "scheduled":
        return "processing";
      case "failed":
        return "error";
      case "sending":
        return "warning";
      default:
        return "default";
    }
  };

  const getEmailStatusIcon = (status) => {
    switch (status) {
      case "sent":
        return <CheckCircleOutlined />;
      case "scheduled":
        return <ClockCircleOutlined />;
      case "failed":
        return <CloseCircleOutlined />;
      case "sending":
        return <SyncOutlined spin />;
      default:
        return <MailOutlined />;
    }
  };

  const emailQueueColumns = [
    {
      title: t("type"),
      dataIndex: "type",
      render: (type) => <span>{type?.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}</span>,
    },
    {
      title: t("status"),
      dataIndex: "status",
      render: (status) => (
        <Tag icon={getEmailStatusIcon(status)} color={getEmailStatusColor(status)}>
          {status?.toUpperCase()}
        </Tag>
      ),
      align: "center",
    },
    {
      title: t("email"),
      dataIndex: "email",
      render: (email, record) => (
        <div>
          <div>{email}</div>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {record.firstName} {record.lastName}
          </Text>
        </div>
      ),
    },
    {
      title: t("subject"),
      dataIndex: "subject",
      render: (subject) => <Text ellipsis={{ tooltip: subject }}>{subject}</Text>,
    },
    {
      title: t("scheduled_for"),
      dataIndex: "scheduledFor",
      render: (timestamp) => (
        <span style={{ whiteSpace: "nowrap" }}>
          {timestamp ? dayjs.unix(timestamp).format(DATE_FORMAT_DD_MM_YYYY_HH_mm) : "-"}
        </span>
      ),
      align: "center",
    },
    {
      title: t("retry_count"),
      dataIndex: "retryCount",
      render: (count) => <span>{count || 0}</span>,
      align: "center",
    },
    {
      title: t("created_at"),
      dataIndex: "createdAt",
      render: (timestamp) => (
        <span style={{ whiteSpace: "nowrap" }}>
          {timestamp ? dayjs.unix(timestamp).format(DATE_FORMAT_DD_MM_YYYY_HH_mm) : "-"}
        </span>
      ),
      align: "center",
    },
  ];

  const orderDetailsColumns = [
    {
      title: t("name"),
      render: (_, data) => (
        <div className="d-flex flex-column">
          <span>
            {data.name} {data.code ? "(" + data.code + ")" : "(" + getProductCodeById(data.productId, products) + ")"}
          </span>
          {data.preOrderLine && (
            <Tag color="orange" style={{ marginTop: 4, width: "fit-content" }}>
              {t("pre_order")}
            </Tag>
          )}
          {data.unavailableLine && (
            <Tag color="gold" style={{ marginTop: 4, width: "fit-content" }}>
              {t("unavailable_item")}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: t("price"),
      dataIndex: "originalPrice",
      render: (data) => <span style={{ whiteSpace: "nowrap" }}>{CURRENCY + formatPrice(data)}</span>,
      align: "center",
    },
    {
      title: t("quantity"),
      dataIndex: "quantity",
      render: (data) => <span>{data}</span>,
      align: "center",
    },
    {
      title: t("discount"),
      dataIndex: "discountPercentage",
      render: (data) => <span>{data}%</span>,
      align: "center",
    },
    {
      title: t("sub_total"),
      dataIndex: "paymentPrice",
      render: (_, data) => (
        <span style={{ whiteSpace: "pre-line" }}>{CURRENCY + formatPrice(data.paymentPrice * data.quantity)}</span>
      ),
      align: "right",
    },
  ];

  if (!order) return null;
  const confirmation = getOrderConfirmationStatus(liveOrder ?? order);
  const confirmationError = confirmation.noResult ? t("order.confirmation_mail.no_result") : confirmation.error;
  return (
    <Drawer
      title={order.orderId}
      placement="right"
      onClose={closeDrawer}
      open={open}
      width={isMobile ? window.innerWidth : 720}
      styles={{
        body: {
          padding: isMobile ? "12px" : "24px",
        },
      }}
    >
      {contextHolder}
      <Descriptions
        bordered
        title={
          <div className="d-flex justify-content-between">
            <span>{t("order.order")}</span>
            <div style={{ display: "flex", gap: 8 }}>
              {canManageOrders && (
                <Popconfirm
                  placement="bottom"
                  title={t("confirm_resend_confirmation_mail", { customer: order.customer.company })}
                  onConfirm={() => resendConfirmationMail(order)}
                  okText={t("yes")}
                  cancelText={t("no")}
                >
                  <Button size="small" icon={<MailOutlined />} className="mr-2">
                    {t("resend_confirmation_mail")}
                  </Button>
                </Popconfirm>
              )}
            </div>
          </div>
        }
        size="small"
        column={1}
      >
        <Descriptions.Item label={t("order.status")}>
          <Tag color={getPaymentStatusColor(order?.status?.current ?? order.orderStatus)}>
            {getPaymentStatus(order?.status?.current ?? order.orderStatus)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t("payment_status")}>
          {(() => {
            const current = (order?.payment?.status?.current || "").toLowerCase();
            return <Tag color={getPaymentStatusColor(current)}>{current ? t(current) : "-"}</Tag>;
          })()}
        </Descriptions.Item>
        <Descriptions.Item label={t("payment_method")}>{t(order?.payment?.method) || "-"}</Descriptions.Item>
        <Descriptions.Item label={t("order_date")}>
          {dayjs.unix(order.createdAt).format(DATE_FORMAT_DD_MM_YYYY_HH_mm)}
        </Descriptions.Item>
        <Descriptions.Item label={t("order.confirmation_mail.label")}>
          {confirmation.status === ORDER_CONFIRMATION_STATUS.UNKNOWN ? (
            "-"
          ) : (
            <>
              <Tag {...CONFIRMATION_TAGS[confirmation.status]}>{t(`order.confirmation_mail.${confirmation.status}`)}</Tag>
              {dayjs.unix(confirmation.at).format(DATE_FORMAT_DD_MM_YYYY_HH_mm)}
              {confirmationError && (
                <div>
                  <Text type="danger">{confirmationError}</Text>
                </div>
              )}
              {confirmation.previouslySentAt && (
                <div>
                  <Text type="secondary">
                    {t("order.confirmation_mail.previously_sent", {
                      date: dayjs.unix(confirmation.previouslySentAt).format(DATE_FORMAT_DD_MM_YYYY_HH_mm),
                    })}
                  </Text>
                </div>
              )}
            </>
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t("company")}>{order.customer.company}</Descriptions.Item>
        <Descriptions.Item label={t("email")}>{order.customer.email}</Descriptions.Item>
        <Descriptions.Item label={t("phone")}>{order.customer.phoneNumber}</Descriptions.Item>
        <Descriptions.Item label={t("delivery_address")}>
          {order.customer.address.delivery.firstName} {order.customer.address.delivery.lastName} <br />
          {order.customer.address.delivery.companyName} {order.customer.address.delivery.companyName && <br />}
          {order.customer.address.delivery.street} {order.customer.address.delivery.houseNumber}{" "}
          {order.customer.address.delivery.addition}
          <br />
          {order.customer.address.delivery.zipCode}, {order.customer.address.delivery.city}
        </Descriptions.Item>
        {!!order.customer.address.invoice && (
          <Descriptions.Item label={t("invoice_address")}>
            {order.customer.address.invoice.firstName} {order.customer.address.invoice.lastName} <br />
            {order.customer.address.invoice.companyName} {order.customer.address.invoice.companyName && <br />}
            {order.customer.address.invoice.street} {order.customer.address.invoice.houseNumber}{" "}
            {order.customer.address.invoice.addition}
            <br />
            {order.customer.address.invoice.zipCode}, {order.customer.address.invoice.city}
          </Descriptions.Item>
        )}
        <Descriptions.Item label={t("partial_delivery")}>
          {!!order.delivery.partialDelivery ? t("yes") : t("no")}
        </Descriptions.Item>
        <Descriptions.Item label={t("preferred_delivery_date")}>
          {!!order.delivery.preferredDateTimestamp
            ? dayjs.unix(order.delivery.preferredDateTimestamp).format("dddd D MMMM")
            : "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("delivery_from")}>
          {!!order.delivery.expectedShippingDate ? order.delivery.expectedShippingDate : "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("order_notes")}>{!!order.orderNotes ? order.orderNotes : "-"}</Descriptions.Item>
        <Descriptions.Item label={t("sales_person")}>
          {!!order.salesPersonName ? order.salesPersonName : "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("order_placed_by")}>
          <Tag color={order.orderPlacedBySalesPerson ? "purple" : "green"}>
            {order.orderPlacedBySalesPerson ? t("placed_by_seller") : t("placed_by_customer")}
          </Tag>
        </Descriptions.Item>
      </Descriptions>

      {/* Shipment Information */}
      {order.shipmentIds && order.shipmentIds.length > 0 && (
        <Descriptions
          bordered
          title={
            <div className="d-flex justify-content-between align-items-center">
              <span>{shipments.length > 1 ? t("shipment.shipments") : t("shipment.shipment")}</span>
            </div>
          }
          size="small"
          column={1}
          style={{ marginTop: 16 }}
        >
          {loadingShipments ? (
            <Descriptions.Item label={t("status")}>
              <Tag icon={<SyncOutlined spin />} color="processing">
                {t("loading")}
              </Tag>
            </Descriptions.Item>
          ) : shipments.length > 0 ? (
            shipments.map((shipment, index) => (
              <React.Fragment key={shipment.id}>
                {shipments.length > 1 && (
                  <Descriptions.Item label={`${t("shipment.shipment")} ${index + 1}`}>
                    <Text strong>{shipment.orderId}</Text>
                  </Descriptions.Item>
                )}
                <Descriptions.Item label={t("shipment.status")}>
                  <Tag
                    color={
                      shipment.status?.current === "DELIVERED"
                        ? "green"
                        : shipment.status?.current === "CANCELLED"
                          ? "red"
                          : shipment.status?.current === "IN_TRANSIT"
                            ? "orange"
                            : shipment.status?.current === "PICKED_UP"
                              ? "blue"
                              : "default"
                    }
                  >
                    {shipment.status?.current === "CREATED" && t("shipment.status_created")}
                    {shipment.status?.current === "PICKED_UP" && t("shipment.status_picked_up")}
                    {shipment.status?.current === "IN_TRANSIT" && t("shipment.status_in_transit")}
                    {shipment.status?.current === "DELIVERED" && t("shipment.status_delivered")}
                    {shipment.status?.current === "CANCELLED" && t("shipment.status_cancelled")}
                  </Tag>
                </Descriptions.Item>
                {shipment.trackingUrl && (
                  <Descriptions.Item label={t("shipment.tracking")}>
                    <a href={shipment.trackingUrl} target="_blank" rel="noopener noreferrer">
                      📦 Track & Trace
                    </a>
                  </Descriptions.Item>
                )}
                {shipment.deliveryDate && (
                  <Descriptions.Item label={t("shipment.delivery_date")}>
                    {dayjs(shipment.deliveryDate).format("DD-MM-YYYY")}
                    {shipment.deliveryTimeFrom && shipment.deliveryTimeTill && (
                      <span style={{ marginLeft: 8, color: "#888" }}>
                        ({shipment.deliveryTimeFrom} - {shipment.deliveryTimeTill})
                      </span>
                    )}
                  </Descriptions.Item>
                )}
                {index < shipments.length - 1 && (
                  <Descriptions.Item>
                    <div style={{ borderTop: "1px dashed #d9d9d9", margin: "8px 0" }} />
                  </Descriptions.Item>
                )}
              </React.Fragment>
            ))
          ) : (
            <Descriptions.Item label={t("status")}>
              <Text type="secondary">{t("shipment.not_found")}</Text>
            </Descriptions.Item>
          )}
        </Descriptions>
      )}
      <div className="pt-5">
        <h4>{t("products")}</h4>
        {isMobile ? (
          <>
            <Space direction="vertical" size={8} style={{ width: "100%", marginBottom: 16 }}>
              {order.products.map((product) => (
                <ProductItemCard key={product.variantId}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                      {product.name}
                      {product.preOrderLine && (
                        <Tag color="orange" style={{ marginLeft: 8 }}>
                          {t("pre_order")}
                        </Tag>
                      )}
                      {product.unavailableLine && (
                        <Tag color="gold" style={{ marginLeft: 8 }}>
                          {t("unavailable_item")}
                        </Tag>
                      )}
                    </div>
                    <div style={{ fontSize: 12, marginBottom: 4 }}>
                      {product.code || getProductCodeById(product.productId, products)}
                    </div>
                    <div style={{ fontSize: 12, color: "#666" }}>
                      {product.quantity}x {CURRENCY}
                      {formatPrice(product.originalPrice)}
                      {product.discountPercentage > 0 && (
                        <span style={{ marginLeft: 8, color: colors.cta }}>-{product.discountPercentage}%</span>
                      )}
                    </div>
                  </div>
                  <div style={{ fontWeight: 600, color: colors.cta }}>
                    {CURRENCY}
                    {formatPrice(product.paymentPrice * product.quantity)}
                  </div>
                </ProductItemCard>
              ))}
            </Space>
            <OrderSummary>
              {order.delivery.type === DELIVERY_OPTIONS.SEND && (
                <SummaryRow>
                  <span>{t("shipping")}</span>
                  <span>
                    {CURRENCY}
                    {formatPrice(order.delivery.cost || 0)}
                  </span>
                </SummaryRow>
              )}
              {!!order.discount && (
                <SummaryRow>
                  <span>
                    {!!order.discount.percentage ? (
                      <Trans
                        i18nKey="cart_discount_percentage"
                        values={{
                          discountPercentage: order.discount.percentage,
                          discountCode: order.discount.code,
                        }}
                      />
                    ) : (
                      <Trans
                        i18nKey="cart_discount_amount"
                        values={{
                          discountAmount: order.discount.amount,
                          discountCode: order.discount.code,
                        }}
                      />
                    )}
                  </span>
                  <span>
                    - {CURRENCY}
                    {formatPrice(order.discount.totalDiscount)}
                  </span>
                </SummaryRow>
              )}
              {!!orderTax && (
                <SummaryRow>
                  <span>{t("vat")}</span>
                  <span>
                    {CURRENCY}
                    {formatPrice(orderTax.taxPrice)}
                  </span>
                </SummaryRow>
              )}
              <SummaryRow style={{ borderTop: "2px solid " + colors.cta, paddingTop: 12, marginTop: 8 }}>
                <span style={{ fontSize: 16, fontWeight: 700 }}>{t("total")}</span>
                <span style={{ fontSize: 18, fontWeight: 700, color: colors.cta }}>
                  {CURRENCY}
                  {formatPrice(!!orderTax ? orderTax.totalPriceInclTax : order.totalPrice)}
                </span>
              </SummaryRow>
            </OrderSummary>
          </>
        ) : (
          <div className="table-responsive">
            <Table
              columns={orderDetailsColumns}
              dataSource={order.products}
              pagination={false}
              rowKey="variantId"
              className="w-100"
              bordered={true}
              summary={(pageData) => {
                let totalAmount = 0;
                let totalDiscount = 0;

                pageData.forEach(({ quantity, discount }) => {
                  totalAmount += quantity;
                  totalDiscount += discount;
                });
                return (
                  !!order && (
                    <>
                      {order.delivery.type === DELIVERY_OPTIONS.SEND && (
                        <Table.Summary.Row>
                          <Table.Summary.Cell>
                            <Text>{t("shipping")}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell />
                          <Table.Summary.Cell />
                          <Table.Summary.Cell />
                          <Table.Summary.Cell align="right">
                            <Text>{!!order.delivery.cost ? "€" + formatPrice(order.delivery.cost) : "-"}</Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      )}
                      {!!order.discount && (
                        <Table.Summary.Row>
                          <Table.Summary.Cell>
                            <Text>
                              {!!order.discount.percentage ? (
                                <Trans
                                  i18nKey="cart_discount_percentage"
                                  values={{
                                    discountPercentage: order.discount.percentage,
                                    discountCode: order.discount.code,
                                  }}
                                />
                              ) : (
                                <Trans
                                  i18nKey="cart_discount_amount"
                                  values={{
                                    discountAmount: order.discount.amount,
                                    discountCode: order.discount.code,
                                  }}
                                />
                              )}
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell />
                          <Table.Summary.Cell />
                          <Table.Summary.Cell />
                          <Table.Summary.Cell align="right">
                            <Text>- {CURRENCY + formatPrice(order.discount.totalDiscount)}</Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      )}
                      <Table.Summary.Row>
                        <Table.Summary.Cell>
                          <Text>{t("shipping")}</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell />
                        <Table.Summary.Cell />
                        <Table.Summary.Cell />
                        <Table.Summary.Cell align="right">
                          <Text>{CURRENCY + formatPrice(!!order.delivery.cost ? order.delivery.cost : 0)}</Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                      {!!orderTax ? (
                        <>
                          <Table.Summary.Row>
                            <Table.Summary.Cell>
                              <Text>{t("vat")}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell align="center" />
                            <Table.Summary.Cell align="center" />
                            <Table.Summary.Cell align="center" />
                            <Table.Summary.Cell align="right">
                              <Text>{CURRENCY + formatPrice(orderTax.taxPrice)}</Text>
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                          <Table.Summary.Row>
                            <Table.Summary.Cell>
                              <Text strong={true}>{t("total")}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell align="center" />
                            <Table.Summary.Cell align="center">
                              <Text strong={true}>{totalAmount}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell align="center">
                              <Text strong={true}>{CURRENCY + formatPrice(totalDiscount)}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell align="right">
                              <Text strong={true}>{CURRENCY + formatPrice(orderTax.totalPriceInclTax)}</Text>
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        </>
                      ) : (
                        <Table.Summary.Row>
                          <Table.Summary.Cell>
                            <Text strong={true}>{t("total")}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell align="center" />
                          <Table.Summary.Cell align="center">
                            <Text strong={true}>{totalAmount}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell align="center">
                            <Text strong={true}>{CURRENCY + formatPrice(totalDiscount)}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell align="right">
                            <Text strong={true}>{CURRENCY + formatPrice(order.totalPrice)}</Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      )}
                    </>
                  )
                );
              }}
            />
          </div>
        )}
      </div>
      {order.emailQueue && order.emailQueue.length > 0 && (
        <div className="pt-5">
          <h4>{t("email_queue")}</h4>
          <Collapse
            items={order.emailQueue.map((email) => ({
              key: email.id,
              label: (
                <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 500 }}>{t(email.type)}</span>
                  <Tag icon={getEmailStatusIcon(email.status)} color={getEmailStatusColor(email.status)}>
                    {t(email.status)}
                  </Tag>
                </div>
              ),
              children: (
                <Descriptions bordered size="small" column={1}>
                  <Descriptions.Item label={t("email")}>{email.recipient}</Descriptions.Item>
                  <Descriptions.Item label={t("name")}>
                    {email.firstName} {email.lastName}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("subject")}>{email.subject}</Descriptions.Item>
                  <Descriptions.Item label={t("type")}>{t(email.type)}</Descriptions.Item>
                  <Descriptions.Item label={t("status")}>
                    <Tag icon={getEmailStatusIcon(email.status)} color={getEmailStatusColor(email.status)}>
                      {t(email.status)}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label={t("scheduled_for")}>
                    {email.scheduledFor ? dayjs.unix(email.scheduledFor).format(DATE_FORMAT_DD_MM_YYYY_HH_mm) : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("created_at")}>
                    {email.createdAt ? dayjs.unix(email.createdAt).format(DATE_FORMAT_DD_MM_YYYY_HH_mm) : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("retry_count")}>{email.retryCount || 0}</Descriptions.Item>
                  {email.failureReason && email.status === "failed" && (
                    <Descriptions.Item label={t("failure_reason")}>
                      <Text type="danger">{email.failureReason}</Text>
                    </Descriptions.Item>
                  )}
                  {email.nextRetry && ["failed", "scheduled"].includes(email.status) && (
                    <Descriptions.Item label={t("next_retry")}>
                      {dayjs.unix(email.nextRetry).format(DATE_FORMAT_DD_MM_YYYY_HH_mm)}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ),
            }))}
          />
        </div>
      )}
    </Drawer>
  );
};

const ProductItemCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #f9f9f9;
  border-radius: 6px;
  border: 1px solid #f0f0f0;
`;

const OrderSummary = styled.div`
  padding: 16px;
  background: linear-gradient(135deg, #f5f5f5 0%, #fafafa 100%);
  border-radius: 8px;
  border: 2px solid ${colors.cta};
  box-shadow: 0 2px 8px rgba(167, 76, 0, 0.1);
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  font-size: 14px;

  &:not(:last-child) {
    border-bottom: 1px solid #e8e8e8;
  }
`;

export default OrderDrawer;
